import { createSelector } from "reselect";

import syncApplications, { syncApplicationNames } from "../../constants/syncApplications";
import SyncCustomFieldOptions from "../../constants/SyncCustomFieldOptions";
import syncDocumentTypes, { getDefaultIconForSyncType } from "../../constants/syncDocumentTypes";
import syncFieldTypes from "../../constants/syncFieldTypes";
import { calendarSelectors } from "../../modules/definition/calendar";
import { itemSelectors } from "../../modules/items";
import { syncSelectors } from "../../modules/sync";
import { syncSettingsSelectors } from "../../modules/sync/settings";
import { immutableRecords } from "../../types";
import { getLabelWithUntitled } from "../../utils/labelUtils";

const ulyssesPropertyOptions = [
  { label: "Ulysses Note", value: SyncCustomFieldOptions.ULYSSES_NOTE },
  { label: "None", value: SyncCustomFieldOptions.NONE }
];

// const ulyssesEntityOptions = [
//   { label: "Ulysses Keywords", value: SyncCustomFieldOptions.ULYSSES_KEYWORD },
//   { label: "None", value: SyncCustomFieldOptions.NONE }
// ];

const ulyssesLabelOptions = [
  { label: "Ulysses Title", value: SyncCustomFieldOptions.TITLE },
  { label: "None", value: SyncCustomFieldOptions.NONE }
];

const scrivenerLabelOptions = [
  { label: "Scrivener: Title", value: SyncCustomFieldOptions.TITLE },
  { label: "None", value: SyncCustomFieldOptions.NONE }
];

const scrivenerSummaryOptions = [
  { label: "Scrivener: Synopsis", value: SyncCustomFieldOptions.SYNOPSIS },
  { label: "None", value: SyncCustomFieldOptions.NONE }
];

const getSyncFields = state => state.sync.settings.fields;

const getSyncEnabledForFieldId = (state, id) => getSyncFields(state).has(id);

const getFolderForFieldId = (state, id) =>
  getSyncFields(state).has(id) ? getSyncFields(state).get(id).folderId : null;
const getCustomFieldIdForFieldId = (state, id) =>
  getSyncFields(state).has(id) ? getSyncFields(state).get(id).customFieldId : null;

const getScrivenerOptionsForDateField = state => {
  const types = syncSelectors.getSyncFieldTypesForCalendarAndRange(
    calendarSelectors.getCalendar(state),
    itemSelectors.getTimestampRange(state),
    syncSelectors.getSyncProjectIsScrivener2(state)
  );
  return syncSelectors
    .getCustomFieldsOfTypes(state, types)
    .map((f, value) => {
      return { label: f.label, value };
    })
    .toList()
    .push({ label: "Create New", value: SyncCustomFieldOptions.CREATE })
    .insert(0, { label: "None", value: SyncCustomFieldOptions.NONE })
    .toArray();
};

const getScrivenerOptionsForCustomFieldOfType = (state, type) => {
  const isScrivener2 = syncSelectors.getSyncProjectIsScrivener2(state);
  let canCreate = !isScrivener2 || type === syncFieldTypes.TEXT || type === syncFieldTypes.MULTITEXT;
  let result = syncSelectors
    .getCustomFieldsOfType(state, type)
    .map((f, value) => {
      return { label: f.label, value };
    })
    .toList()
    .insert(0, { label: "None", value: SyncCustomFieldOptions.NONE });

  if (canCreate) {
    result = result.push({ label: "Create New", value: SyncCustomFieldOptions.CREATE });
  }

  return result.toArray();
};

const getOptionsForCustomFieldOfType = (state, type) => {
  const app = syncSettingsSelectors.getSyncApplication(state);
  if (app === syncApplications.SCRIVENER) {
    return getScrivenerOptionsForCustomFieldOfType(state, type);
  }
  return ulyssesPropertyOptions;
};

const getOptionsForDateField = state => {
  const app = syncSettingsSelectors.getSyncApplication(state);
  if (app === syncApplications.SCRIVENER) {
    return getScrivenerOptionsForDateField(state);
  }
  return ulyssesPropertyOptions;
};

const getOptionsForLabelField = state => {
  const app = syncSettingsSelectors.getSyncApplication(state);
  if (app === syncApplications.SCRIVENER) {
    return scrivenerLabelOptions;
  }
  return ulyssesLabelOptions;
};

const getOptionsForSummaryField = state => {
  const app = syncSettingsSelectors.getSyncApplication(state);
  if (app === syncApplications.SCRIVENER) {
    return scrivenerSummaryOptions;
  }
  return ulyssesPropertyOptions;
};

const displayTreeItemForItem = (treeItem, index, externalItems) => {
  const doc = externalItems.get(treeItem.id);
  const type = doc ? doc.type : syncDocumentTypes.FOLDER;

  if (type !== syncDocumentTypes.FOLDER) {
    return null;
  }

  // First, convert the children so we know what statuses are bubbling up from underneath
  const id = treeItem.id;
  const label = doc ? doc.label : syncApplicationNames.ULYSSES;
  const children = treeItem.children.map((c, i) => displayTreeItemForItem(c, i, externalItems)).filter(i => i);
  const icon = getDefaultIconForSyncType(type, children.size > 0);

  return immutableRecords.TreeViewItemRecord({
    id,
    label,
    children,
    icon,
    index
  });
};

const getTreeViewItemsForRep = createSelector(
  // Inputs
  externalRep => externalRep,
  // Output
  externalRep => {
    return displayTreeItemForItem(externalRep.documentTree, 0, externalRep.documents).children.filter(i => i);
  }
);

const folderOptionsForTree = (treeItem, externalItems, foldersOnly) => {
  const doc = externalItems.get(treeItem.id);
  const type = doc ? doc.type : syncDocumentTypes.FOLDER;

  if (
    foldersOnly &&
    type !== syncDocumentTypes.FOLDER &&
    type !== syncDocumentTypes.DRAFT_FOLDER &&
    type !== syncDocumentTypes.RESEARCH_FOLDER
  ) {
    return null;
  }

  const id = treeItem.id;
  const label = doc ? getLabelWithUntitled(doc.label) : "";
  const children = treeItem.children.map(c => folderOptionsForTree(c, externalItems, foldersOnly)).filter(i => i);

  let result = {
    label,
    value: id
  };
  if (children.size) {
    result.children = children;
  }
  return result;
};

/**
 * @param {SyncRepresentationRecord} externalRep
 * @param {boolean} folderOnly
 */
const getFolderDropdownOptionsForRep = createSelector(
  // Inputs
  (externalRep, _folderOnly) => externalRep,
  (externalRep, folderOnly) => folderOnly,
  // Output
  (externalRep, folderOnly) => {
    return folderOptionsForTree(externalRep.documentTree, externalRep.documents, folderOnly).children.filter(i => i);
  }
);

export default {
  getSyncFields,
  getSyncEnabledForFieldId,
  getFolderForFieldId,
  getCustomFieldIdForFieldId,
  getOptionsForCustomFieldOfType,
  getOptionsForDateField,
  getOptionsForLabelField,
  getOptionsForSummaryField,
  getTreeViewItemsForRep,
  getFolderDropdownOptionsForRep
};
